import * as i0 from '@angular/core';
import { InjectionToken, inject, Injectable, Directive, Pipe, APP_INITIALIZER, NgModule } from '@angular/core';
import { AXEventService, AXEventTypes } from '@acorex/core/events';
import { set, get } from 'lodash-es';
import { of, BehaviorSubject, forkJoin, tap, catchError, finalize, shareReplay, map, switchMap, startWith, firstValueFrom, take, lastValueFrom } from 'rxjs';

const AX_TRANSLATION_CONFIG = new InjectionToken('AX_TRANSLATION_CONFIG', {
    providedIn: 'root',
    factory: () => {
        return AXTranslationDefaultConfig;
    },
});
const AXTranslationDefaultConfig = {
    defaultLang: 'en',
    defaultScope: 'common',
    scopeResolverKey: 'scope',
};
function translationConfig(config = {}) {
    const result = {
        ...AXTranslationDefaultConfig,
        ...config,
    };
    return result;
}

class AXTranslationLoaderDefault {
    getTranslation(options) {
        return of({});
    }
}
const AX_TRANSLATION_LOADER = new InjectionToken('AX_TRANSLATION_LOADER', {
    providedIn: 'root',
    factory: () => {
        return new AXTranslationLoaderDefault();
    },
});

let service;
function translateSync(key, options) {
    return service.translateSync(key, options);
}
class AXTranslationService {
    /**
     * @ignore
     */
    constructor() {
        this.loader = inject(AX_TRANSLATION_LOADER);
        this.config = inject(AX_TRANSLATION_CONFIG);
        this.eventService = inject(AXEventService);
        this.translationCache = {};
        this.ongoingRequests = new Map();
        this.activeLang = new BehaviorSubject(this.getDefaultLang());
        this.langChanges$ = this.activeLang.asObservable();
        this.lastActiveLang = null;
        service = this;
    }
    loadLanguagesAndScopes(languages, scopes) {
        const requests = languages.flatMap((lang) => scopes.map((scope) => {
            // Check if translations are already cached
            if (this.translationCache[lang]?.[scope]) {
                return of(this.translationCache[lang][scope]);
            }
            // Use the new method to handle ongoing requests and loading
            return this.getOrLoadTranslations(lang, scope);
        }));
        return forkJoin(requests);
    }
    getOrLoadTranslations(lang, scope) {
        const requestKey = `${lang}_${scope}`;
        // Return existing observable if the request is already in progress
        if (this.ongoingRequests.has(requestKey)) {
            return this.ongoingRequests.get(requestKey);
        }
        // Load translations if not in cache or ongoing requests
        const translationObservable = this.loader.getTranslation({ lang, scope }).pipe(tap((translations) => this.cacheTranslations(lang, scope, translations)), catchError((error) => {
            console.error(`Error loading translations for lang: ${lang}, scope: ${scope}`, error);
            return of(null);
        }), finalize(() => {
            this.eventService.emitEvent({
                type: AXEventTypes.AXLanguageLoaded,
                payload: lang,
            });
            this.ongoingRequests.delete(requestKey);
        }), shareReplay(1));
        this.ongoingRequests.set(requestKey, translationObservable);
        return translationObservable;
    }
    cacheTranslations(lang, scope, translations) {
        set(this.translationCache, `${lang}.${scope}`, translations);
    }
    getDefaultLang() {
        return this.config.defaultLang;
    }
    getActiveLang() {
        return this.activeLang.getValue();
    }
    setActiveLang(lang) {
        if (lang != this.getActiveLang()) {
            this.lastActiveLang = this.getActiveLang(); // Update last active language
            this.activeLang.next(lang);
            this.eventService.emitEvent({
                type: AXEventTypes.AXLanguageChanged,
                payload: lang,
            });
        }
    }
    isLangAvailable(lang, scope = null) {
        return !this.translationCache[lang] && (!scope || !this.translationCache[lang][scope]);
    }
    load(key, options = {}) {
        const { lang = this.getActiveLang(), scope = this.config.defaultScope } = options;
        // Function to fetch translation
        const fetchTranslation = (language) => {
            // Check if translations are available in the cache
            if (this.translationCache[language]?.[scope]) {
                return of(this.getTranslation(key, language, scope, options.params));
            }
            // Use the new method to handle ongoing requests and loading
            return this.getOrLoadTranslations(language, scope).pipe(map(() => {
                const translation = this.translationCache[language]?.[scope]?.[key] || key;
                return this.getTranslation(translation, language, scope, options.params);
            }), switchMap((translatedText) => {
                if (translatedText === key && language !== this.getDefaultLang()) {
                    // If the translation is not found and the language is not default,
                    // try fetching from the default language
                    return fetchTranslation(this.getDefaultLang());
                }
                return of(translatedText);
            }));
        };
        // First, try to fetch the translation for the requested language
        const translationForRequestedLang = fetchTranslation(lang);
        // If lastActiveLang is available and different from the requested language, use it as a fallback
        if (this.lastActiveLang && this.lastActiveLang !== lang) {
            const translationForLastActiveLang = this.getTranslation(key, this.lastActiveLang, scope, options.params);
            return translationForRequestedLang.pipe(startWith(translationForLastActiveLang));
        }
        return translationForRequestedLang;
    }
    getTranslation(key, lang, scope, params) {
        let translation = (get(this.translationCache, `${lang}.${scope}.${key}`) || key);
        // Replace params like {{ name }}
        if (params) {
            Object.keys(params).forEach((paramKey) => {
                const paramValue = params[paramKey];
                if (typeof translation !== 'string') {
                    console.error(`${key} is not string`, { key, lang, scope, params, translation });
                }
                else {
                    translation = translation.replace(new RegExp(`{{\\s*${paramKey}\\s*}}`, 'g'), paramValue);
                }
            });
        }
        if (typeof translation !== 'string') {
            console.error(`${key} is not string`, { key, lang, scope, params, translation });
        }
        else {
            // Replace translations like {{ t('key') }}
            const translationPattern = /{{\s*t\('([^']+)'\)\s*}}/g;
            translation = translation.replace(translationPattern, (match, innerKey) => {
                return this.getTranslation(innerKey, lang, scope, params);
            });
        }
        return translation;
    }
    translate(key, options) {
        if (options?.lang) {
            return this.load(key, options);
        }
        return this.langChanges$.pipe(startWith(this.getActiveLang()), switchMap((lang) => {
            return this.load(key, { ...options, lang: lang });
        }));
    }
    async translateAsync(key, options) {
        const { lang = this.getActiveLang(), scope = this.config.defaultScope } = options || {};
        let translation = get(this.translationCache, `${lang}.${scope}.${key}`);
        if (!translation) {
            // If the translation is not in the cache, load it
            await firstValueFrom(this.load(key, { lang, scope }).pipe(take(1)));
            translation = get(this.translationCache, `${lang}.${scope}.${key}`);
        }
        // If still not available, try the last active language or default language
        if (!translation && this.lastActiveLang) {
            translation = get(this.translationCache, `${this.lastActiveLang}.${scope}.${key}`);
        }
        if (!translation) {
            translation = get(this.translationCache, `${this.getDefaultLang()}.${scope}.${key}`);
        }
        // Perform parameter replacement
        return this.getTranslation((translation || key), lang, scope, options?.params);
    }
    translateSync(key, options) {
        const { lang = this.getActiveLang(), scope = this.config.defaultScope } = options || {};
        // Check if the translation for the active language is available
        let translation = get(this.translationCache, `${lang}.${scope}.${key}`);
        //
        const loadLang = () => lastValueFrom(this.getOrLoadTranslations(lang, scope));
        // If not available, check lastActiveLang
        if (!translation && this.lastActiveLang) {
            loadLang();
            translation = get(this.translationCache, `${this.lastActiveLang}.${scope}.${key}`);
        }
        // If still not available, use the default language
        if (!translation) {
            loadLang();
            translation = get(this.translationCache, `${this.getDefaultLang()}.${scope}.${key}`);
        }
        // Perform parameter replacement
        return this.getTranslation((translation || key), lang, scope, options?.params);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXTranslationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXTranslationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXTranslationService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [] });

const loadTranslationScope = (route, state) => {
    const translatorService = inject(AXTranslationService);
    const config = inject(AX_TRANSLATION_CONFIG);
    const scopeValue = route.data[config.scopeResolverKey];
    const scopes = Array.isArray(scopeValue) ? scopeValue : [scopeValue];
    return translatorService.loadLanguagesAndScopes([translatorService.getActiveLang()], scopes);
};

class AXTranslatorDirective {
    constructor(templateRef, viewContainer, translationService) {
        this.templateRef = templateRef;
        this.viewContainer = viewContainer;
        this.translationService = translationService;
    }
    ngOnInit() {
        this.viewContainer.clear();
        this.viewContainer.createEmbeddedView(this.templateRef, {
            $implicit: (key, options) => {
                return this.translationService.translate(key, options);
            },
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXTranslatorDirective, deps: [{ token: i0.TemplateRef }, { token: i0.ViewContainerRef }, { token: AXTranslationService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.2", type: AXTranslatorDirective, selector: "[translate]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXTranslatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[translate]',
                }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }, { type: i0.ViewContainerRef }, { type: AXTranslationService }] });

class AXTranslatorPipe {
    constructor(service) {
        this.service = service;
    }
    transform(key, options) {
        if (!key) {
            return of(''); // Return an empty observable if the key is not provided
        }
        return this.service.translate(key, options);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXTranslatorPipe, deps: [{ token: AXTranslationService }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.0.2", ngImport: i0, type: AXTranslatorPipe, name: "translate" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXTranslatorPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'translate', pure: true }]
        }], ctorParameters: () => [{ type: AXTranslationService }] });

function initializeApp(translatorService, config) {
    return () => {
        return translatorService.loadLanguagesAndScopes(config.preloadLangs ?? [config.defaultLang], config.preloadScopes ?? [config.defaultScope]);
    };
}
class AXTranslationModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXTranslationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.0.2", ngImport: i0, type: AXTranslationModule, declarations: [AXTranslatorPipe, AXTranslatorDirective], exports: [AXTranslatorPipe, AXTranslatorDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXTranslationModule, providers: [
            {
                provide: APP_INITIALIZER,
                useFactory: initializeApp,
                deps: [AXTranslationService, AX_TRANSLATION_CONFIG],
                multi: true,
            },
        ] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXTranslationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                    exports: [AXTranslatorPipe, AXTranslatorDirective],
                    declarations: [AXTranslatorPipe, AXTranslatorDirective],
                    providers: [
                        {
                            provide: APP_INITIALIZER,
                            useFactory: initializeApp,
                            deps: [AXTranslationService, AX_TRANSLATION_CONFIG],
                            multi: true,
                        },
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AXTranslationDefaultConfig, AXTranslationLoaderDefault, AXTranslationModule, AXTranslationService, AXTranslatorDirective, AXTranslatorPipe, AX_TRANSLATION_CONFIG, AX_TRANSLATION_LOADER, loadTranslationScope, translateSync, translationConfig };
